import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Check, Headphones, Languages, LockKeyhole, Play, Square, Volume2,
} from "lucide-react";
import { cn } from "../../lib/utils";
import {
  VOICE_AIRA_LANGUAGE_EXAMPLES, voiceAiraAudioSource,
} from "./voiceAiraScenarios";

const WAVE_HEIGHTS = [8, 14, 20, 11, 17, 24, 13, 19, 10, 22, 15, 9];
const SAMPLE_COUNT = VOICE_AIRA_LANGUAGE_EXAMPLES.reduce(
  (total, language) => total + language.examples.length,
  0,
);

function Waveform({ active, reduce }) {
  return (
    <span className="flex h-7 items-center gap-[2px]" aria-hidden="true">
      {WAVE_HEIGHTS.map((height, index) => (
        <span
          key={`${height}-${index}`}
          className={cn(
            "block w-[2px] rounded-full bg-current transition-[height,opacity] duration-200",
            active ? "opacity-90" : "opacity-35",
            active && !reduce ? "animate-pulse" : "",
          )}
          style={{ height: active ? height : Math.max(5, Math.round(height * 0.45)), animationDelay: `${index * 55}ms` }}
        />
      ))}
    </span>
  );
}

/**
 * A shared, interaction-loaded library of reviewed demonstration scripts. Every clip is committed
 * AAC audio generated from fictional text; the browser receives no media request until Play.
 */
export default function VoiceAiraAudioLibrary({
  className,
  compact = false,
  defaultLanguage = "te",
  headingLevel = 3,
  reduce = false,
  testId = "voice-aira-audio-library",
}) {
  const initialLanguage = VOICE_AIRA_LANGUAGE_EXAMPLES.some(({ code }) => code === defaultLanguage)
    ? defaultLanguage
    : VOICE_AIRA_LANGUAGE_EXAMPLES[0].code;
  const [activeCode, setActiveCode] = useState(initialLanguage);
  const [activeClipId, setActiveClipId] = useState(null);
  const [status, setStatus] = useState("Choose a sample to hear its synthetic demonstration voice.");
  const audioRef = useRef(null);
  const playbackRequestRef = useRef(0);
  const Heading = headingLevel === 2 ? "h2" : "h3";
  const language = useMemo(
    () => VOICE_AIRA_LANGUAGE_EXAMPLES.find(({ code }) => code === activeCode)
      || VOICE_AIRA_LANGUAGE_EXAMPLES[0],
    [activeCode],
  );

  const stopAudio = (nextStatus = "Audio stopped.") => {
    playbackRequestRef.current += 1;
    const audio = audioRef.current;
    if (audio) {
      const hadSource = audio.hasAttribute("src");
      audio.pause();
      audio.removeAttribute("src");
      if (hadSource) audio.load?.();
    }
    setActiveClipId(null);
    setStatus(nextStatus);
  };

  useEffect(() => () => {
    playbackRequestRef.current += 1;
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute("src");
    }
  }, []);

  const selectLanguage = (code) => {
    if (code === activeCode) return;
    stopAudio();
    setActiveCode(code);
    const next = VOICE_AIRA_LANGUAGE_EXAMPLES.find((item) => item.code === code);
    setStatus(`Showing three ${next?.english || "language"} audio examples.`);
  };

  const toggleClip = async (example) => {
    const clipId = `${language.code}-${example.id}`;
    if (activeClipId === clipId) {
      stopAudio(`${language.english} sample stopped.`);
      return;
    }
    const audio = audioRef.current;
    if (!audio || typeof audio.play !== "function") {
      setStatus("This browser cannot play the demonstration audio. The full transcript remains available.");
      return;
    }
    const playbackRequest = playbackRequestRef.current + 1;
    playbackRequestRef.current = playbackRequest;
    audio.pause();
    audio.src = voiceAiraAudioSource(language.code, example.id);
    audio.preload = "none";
    audio.currentTime = 0;
    setActiveClipId(clipId);
    setStatus(`Loading the ${example.context.toLowerCase()} sample in ${language.english}.`);
    try {
      await audio.play();
      if (audioRef.current === audio && playbackRequestRef.current === playbackRequest) {
        setStatus(`Playing the ${example.context.toLowerCase()} sample in ${language.english}.`);
      }
    } catch {
      if (audioRef.current === audio && playbackRequestRef.current === playbackRequest) {
        setActiveClipId(null);
        setStatus("The sample could not play. Its exact transcript and English meaning remain available.");
      }
    }
  };

  return (
    <div
      className={cn(
        "relative isolate overflow-hidden rounded-[30px] border border-[#332a3b] bg-[#17111f] text-white shadow-[0_30px_85px_-52px_rgba(50,27,70,0.9)]",
        className,
      )}
      data-testid={testId}
    >
      <div className="pointer-events-none absolute inset-0 opacity-60 [background-image:radial-gradient(circle_at_8%_12%,rgba(124,58,237,0.3),transparent_30%),radial-gradient(circle_at_96%_5%,rgba(29,78,216,0.19),transparent_28%)]" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.09] [background-image:linear-gradient(rgba(255,255,255,.2)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.2)_1px,transparent_1px)] [background-size:58px_58px] [mask-image:linear-gradient(to_bottom,black,transparent_62%)]" aria-hidden="true" />

      <div className={cn("relative grid gap-5 border-b border-white/10 p-5 sm:p-7 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end", compact ? "lg:p-7" : "lg:p-9")}>
        <div>
          <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[#c9b7ff]">
            <Headphones className="h-4 w-4" aria-hidden="true" /> Listen across India
          </p>
          <Heading className={cn("mt-3 max-w-[17ch] font-heading font-extrabold leading-[1.04] tracking-[-0.035em]", compact ? "text-[27px] sm:text-[34px]" : "text-[31px] sm:text-[42px]")}>
            Three school examples in every supported language.
          </Heading>
          <p className="mt-3 max-w-[65ch] text-[12.5px] leading-[1.7] text-white/62 sm:text-[13.5px]">
            Compare a parent meeting, an admissions question and a staff follow-up. Every short clip is paired with its exact native-script transcript and English meaning.
          </p>
        </div>
        <div className="grid w-fit grid-cols-2 gap-x-7 gap-y-2 border-l border-white/15 pl-5 lg:text-right">
          <div>
            <p className="font-heading text-[25px] font-extrabold tabular-nums">{SAMPLE_COUNT}</p>
            <p className="text-[10px] font-semibold text-white/50">audio samples</p>
          </div>
          <div>
            <p className="font-heading text-[25px] font-extrabold tabular-nums">11</p>
            <p className="text-[10px] font-semibold text-white/50">languages</p>
          </div>
        </div>
      </div>

      <div className="relative grid lg:grid-cols-[230px_minmax(0,1fr)]">
        <aside className="border-b border-white/10 p-4 sm:p-5 lg:border-b-0 lg:border-r" aria-label="Choose a demonstration language">
          <label htmlFor={`${testId}-language-select`} className="mb-2 block text-[10px] font-bold uppercase tracking-[0.14em] text-white/55 sm:hidden">Language</label>
          <select
            id={`${testId}-language-select`}
            data-testid="voice-audio-language-select"
            value={activeCode}
            onChange={(event) => selectLanguage(event.target.value)}
            className="min-h-12 w-full rounded-xl border border-white/15 bg-white/[0.08] px-3 text-[13px] font-semibold text-white outline-none focus-visible:ring-2 focus-visible:ring-[#b69bff] sm:hidden"
          >
            {VOICE_AIRA_LANGUAGE_EXAMPLES.map((item) => (
              <option key={item.code} value={item.code} className="bg-[#17111f]">{item.native} · {item.english}</option>
            ))}
          </select>

          <div className="hidden gap-1.5 sm:grid sm:grid-cols-3 lg:grid-cols-1" aria-label="Voice sample languages">
            {VOICE_AIRA_LANGUAGE_EXAMPLES.map((item) => {
              const selected = item.code === activeCode;
              return (
                <button
                  key={item.code}
                  type="button"
                  data-testid={`voice-audio-language-${item.code}`}
                  onClick={() => selectLanguage(item.code)}
                  aria-pressed={selected}
                  className={cn(
                    "min-h-11 rounded-xl px-3 py-2 text-left transition-[background-color,color,transform] duration-200 active:scale-[0.985] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#b69bff]",
                    selected ? "bg-[#ffffff] text-[#211829]" : "text-white/62 hover:bg-white/[0.08] hover:text-white",
                  )}
                >
                  <span lang={item.htmlLang} className="block text-[12px] font-bold leading-tight">{item.native}</span>
                  <span className={cn("mt-0.5 block text-[9px] font-semibold", selected ? "text-[#6b6071]" : "text-white/38")}>{item.english}</span>
                </button>
              );
            })}
          </div>
        </aside>

        <section
          className="bg-[#f8f5f0] p-4 text-[#241d29] sm:p-6 lg:p-7 xl:p-8"
          aria-labelledby={`${testId}-active-language`}
        >
          <div className="flex flex-wrap items-end justify-between gap-3 border-b border-[#ded7df] pb-4">
            <div>
              <p className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.14em] text-[#6d28d9]">
                <Languages className="h-3.5 w-3.5" aria-hidden="true" /> Active language
              </p>
              <h4 id={`${testId}-active-language`} className="mt-1.5 font-heading text-[25px] font-extrabold tracking-[-0.025em]">
                <span lang={language.htmlLang}>{language.native}</span>{language.native === language.english ? "" : ` · ${language.english}`}
              </h4>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#c8ddff] bg-[#edf4ff] px-2.5 py-1 text-[9.5px] font-bold text-[#1d4ed8]">
              <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" /> 3 transcripts included
            </span>
          </div>

          <div className="mt-5 grid gap-3 md:grid-cols-3" data-testid="voice-audio-examples">
            {language.examples.map((example, index) => {
              const clipId = `${language.code}-${example.id}`;
              const playing = activeClipId === clipId;
              return (
                <article
                  key={example.id}
                  data-testid="voice-audio-example"
                  className={cn(
                    "flex min-w-0 flex-col rounded-[20px] border bg-[#ffffff] p-4 shadow-[0_18px_40px_-36px_rgba(47,31,58,0.7)] transition-[border-color,transform,box-shadow] duration-200 sm:p-5",
                    playing ? "border-[#9f7aea] shadow-[0_20px_45px_-30px_rgba(109,40,217,0.48)]" : "border-[#e2dbe4] hover:-translate-y-0.5 hover:border-[#cfc1db]",
                  )}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[9px] font-bold uppercase tracking-[0.13em] text-[#6d28d9]">0{index + 1} · {example.context}</p>
                      <p className="mt-1 text-[10px] font-semibold text-[#756b79]">{example.speaker}</p>
                    </div>
                    <Volume2 className={cn("h-4 w-4", playing ? "text-[#6d28d9]" : "text-[#a098a4]")} aria-hidden="true" />
                  </div>

                  <p lang={language.htmlLang} className="mt-5 min-h-[6.75rem] text-[15px] font-semibold leading-[1.65] text-[#2d2532] sm:text-[16px]">
                    {example.nativeText}
                  </p>
                  <div className="mt-4 border-l-2 border-[#d7c8e8] pl-3">
                    <p className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#746a79]">English meaning</p>
                    <p className="mt-1 text-[11px] leading-[1.55] text-[#675e6b]">{example.englishMeaning}</p>
                  </div>

                  <div className="mt-auto flex items-center justify-between gap-3 border-t border-[#ece6ed] pt-4">
                    <button
                      type="button"
                      data-testid="voice-audio-play"
                      data-audio-src={voiceAiraAudioSource(language.code, example.id)}
                      aria-pressed={playing}
                      aria-label={`${playing ? "Stop" : "Play"} ${example.context.toLowerCase()} audio in ${language.english}`}
                      onClick={() => toggleClip(example)}
                      className={cn(
                        "inline-flex min-h-11 items-center gap-2 rounded-full px-3.5 text-[10.5px] font-bold transition-[background-color,color,transform] active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7c3aed]/55",
                        playing ? "bg-[#24182e] text-white" : "bg-[#eee8f4] text-[#5b21b6] hover:bg-[#e6dcf1]",
                      )}
                    >
                      {playing ? <Square className="h-3 w-3" fill="currentColor" aria-hidden="true" /> : <Play className="h-3.5 w-3.5" fill="currentColor" aria-hidden="true" />}
                      {playing ? "Stop" : "Play sample"}
                    </button>
                    <Waveform active={playing} reduce={reduce} />
                  </div>
                </article>
              );
            })}
          </div>

          <div className="mt-5 grid gap-2 border-t border-[#ded7df] pt-4 text-[10px] leading-relaxed text-[#756b79] sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
            <p className="flex items-start gap-2">
              <LockKeyhole className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#6d28d9]" aria-hidden="true" />
              Synthetic demonstration voices from fictional scripts—not customer recordings. Audio loads only after Play.
            </p>
            <p className="font-semibold text-[#5e5363]" role="status" aria-live="polite" data-testid="voice-audio-status">{status}</p>
          </div>
        </section>
      </div>

      <audio
        ref={audioRef}
        preload="none"
        className="hidden"
        aria-hidden="true"
        data-testid="voice-audio-element"
        onEnded={() => {
          playbackRequestRef.current += 1;
          setActiveClipId(null);
          setStatus(`${language.english} sample finished.`);
        }}
        onError={() => {
          if (activeClipId) {
            playbackRequestRef.current += 1;
            setActiveClipId(null);
            setStatus("The sample could not play. Its exact transcript and English meaning remain available.");
          }
        }}
      />
    </div>
  );
}

export { SAMPLE_COUNT };
